import { format, formatDistanceToNowStrict, parseISO } from "date-fns";

const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function formatDate(date: Date | string | number): string {
    return format(new Date(date), "MMM d, yyyy");
}

export function formatDateTime(date: Date | string | number): string {
    return format(new Date(date), "MMM d, yyyy, h:mm a");
}

/** The value a `datetime-local` input expects, in the viewer's time zone. */
export function toDateTimeLocal(date: Date | string | number): string {
    return format(new Date(date), "yyyy-MM-dd'T'HH:mm");
}

export function formatDateShort(date: Date | string | number): string {
    return format(new Date(date), "MMM d");
}

/** "2:30 PM". */
export function formatTime(date: Date | string | number): string {
    return format(new Date(date), "h:mm a");
}

/** "Thursday, October 23": the date line above the home greeting. */
export function formatWeekdayDate(date: Date | string | number): string {
    return format(new Date(date), "EEEE, MMMM d");
}

/** "3 days ago". */
export function formatRelative(date: Date | string | number): string {
    return formatDistanceToNowStrict(new Date(date), { addSuffix: true });
}

// "2026-03-01" or a full ISO timestamp -> "March 2026" ("Mar 2026" short). Only the date part is
// read, as a local date, so a UTC midnight never slips into the prior month.
export function formatMonth(iso: string, style: "long" | "short" = "long"): string {
    const d = parseISO(iso.slice(0, 10));
    return Number.isNaN(d.getTime()) ? "—" : format(d, style === "long" ? "MMMM yyyy" : "MMM yyyy");
}

export function formatCurrency(amount: number): string {
    return currency.format(amount);
}

const compactCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });

const wholeCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** "$1.3M", "$48.2K", "$840": a headline figure, not a ledger amount. */
export function formatCompactCurrency(amount: number): string {
    return Math.abs(amount) < 1000 ? wholeCurrency.format(amount) : compactCurrency.format(amount);
}

/** A calendar day key ("2026-10-09") as "Oct 9", read as a local date so it never slips a day. */
export function formatDay(day: string): string {
    const d = parseISO(day.slice(0, 10));
    return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d");
}

export function formatNumber(value: number): string {
    return number.format(value);
}

/** "1 application", "1,204 applications". Pass `plural` when it isn't the noun plus "s". */
export function formatCount(count: number, singular: string, plural = `${singular}s`): string {
    return `${number.format(count)} ${count === 1 ? singular : plural}`;
}

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${number.format(value)} ${units[unit]}`;
}
